import { useEffect } from 'react'
import { toCanvasPoint } from '@/components/app/editor/tools/shared/helpers/svg-point'
import { snapToAngle45 } from '@/components/app/editor/tools/shared/helpers/snap-to-angle-45'
import { uid } from '@/components/app/editor/tools/shared/helpers/uid'
import { dragHandlePoint } from '../helpers/drag-handle-point'
import { type PenSnapSettings, resolvePenSnap } from '../helpers/resolve-pen-snap'
import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import type { PenDrawingState } from './use-pen-drawing-state'
import type { PenPropRefs } from './use-pen-prop-refs'

/** Screen-pixel distance within which the cursor snaps to / closes on the first anchor point. */
const CLOSE_THRESHOLD_PX = 10

/** Minimum drag distance (screen px) before handle creation begins. */
const HANDLE_MIN_DRAG_PX = 3

function isNearPoint(ax: number, ay: number, bx: number, by: number, scale: number): boolean {
  const threshold = CLOSE_THRESHOLD_PX / scale
  const dx = ax - bx
  const dy = ay - by

  return Math.sqrt(dx * dx + dy * dy) <= threshold
}

/**
 * Window-level pointer handlers — capture events beyond spread bounds. Registered once on mount;
 * every handler reads the latest props and drawing state through refs.
 */
export function usePenPointerListeners(drawing: PenDrawingState, props: PenPropRefs) {
  useEffect(() => {
    const {
      svgRef,
      pointsRef,
      initialPointsLenRef,
      forceUpdate,
      mousePos,
      setMousePosState,
      dragRef,
      setIsNearFirst,
      spaceHeldRef,
      resetDrawing,
      completePath,
      commitInProgressPath,
    } = drawing
    const initialPointsLen = initialPointsLenRef.current

    // Coordinate conversion: client → canvas (SVG user units)
    const toCanvas = (clientX: number, clientY: number) =>
      toCanvasPoint(
        svgRef.current,
        clientX,
        clientY,
        props.bleedWidthRef.current,
        props.bleedHeightRef.current
      )

    const snapSettings = (): PenSnapSettings => ({
      snapToGrid: props.snapToGridRef.current,
      gridSnapStep: props.gridSnapStepRef.current,
      gridSnapLiveRects: props.gridSnapLiveRectsRef.current,
      smartGuidesEnabled: props.smartGuidesEnabledRef.current,
      snapGuideDistance: props.snapGuideDistanceRef.current,
      getShapeBoundsForSmartGuides: props.getShapeBoundsForSmartGuidesRef.current,
    })

    const isInCanvasArea = (target: EventTarget | null): boolean => {
      const scroller = props.canvasScrollerRefRef.current?.current

      if (!scroller) return true // No scroller ref: allow everywhere
      return scroller.contains(target as Node)
    }

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      if (!isInCanvasArea(e.target)) return
      // Let spacebar+drag pass through to the canvas pan handler.
      if (spaceHeldRef.current) return

      const pts = pointsRef.current

      // Only check for an existing shape under the cursor when no drawing is in progress.
      // Once the user has placed at least one point, every subsequent click must place the
      // next anchor — exiting mid-draw because a previously-drawn shape is underneath
      // would deselect that shape and discard the in-progress path.
      if (pts.length === 0) {
        // Use elementsFromPoint for precise geometric hit testing.
        // Bounding-rect checks produce false positives for irregular shapes (empty space
        // inside the bbox would wrongly trigger an exit). elementsFromPoint checks the
        // actual rendered fill/stroke area.
        const elementsAtPoint = document.elementsFromPoint(e.clientX, e.clientY)
        const hasShapeUnder = elementsAtPoint.some(
          (el) => el.hasAttribute('data-editor-shape') || el.hasAttribute('data-editor-shape-path')
        )

        if (hasShapeUnder && !props.skipShapeExitCheckRef.current) {
          props.onExitPenToolRef.current?.()
          return
        }
      }

      e.preventDefault()
      e.stopPropagation()

      const canvasPt = toCanvas(e.clientX, e.clientY)
      // Shift held: snap to nearest 45° from last anchor; smart guides disabled. Otherwise grid,
      // then smart-guide snap. Either way, clear guides immediately — point is being placed;
      // handle-drag follows.
      const { x, y } = resolvePenSnap(
        canvasPt.x,
        canvasPt.y,
        e.shiftKey && pts.length > 0 ? pts[pts.length - 1] : null,
        snapSettings()
      )

      props.onSmartGuidesChangeRef.current?.([])

      // Close path if near the first anchor
      if (pts.length >= 2 && isNearPoint(x, y, pts[0].x, pts[0].y, props.scaleRef.current)) {
        completePath(pts, true)
        return
      }

      // Notify parent that drawing has started (first point only) so it can deselect
      // any previously selected shapes before the new path begins.
      if (pts.length === 0) {
        props.onDrawingStartRef.current?.()
      }

      // Add new anchor point
      const newPt: PenAnchorPoint = {
        id: uid(),
        x,
        y,
        type: props.defaultPointTypeRef.current,
        handleIn: null,
        handleOut: null,
      }

      const newPts = [...pts, newPt]

      pointsRef.current = newPts
      dragRef.current = {
        pointIndex: newPts.length - 1,
        startX: newPt.x,
        startY: newPt.y,
        hasMoved: false,
      }
      forceUpdate((n) => n + 1)
    }

    const onPointerMove = (e: PointerEvent) => {
      let { x, y } = toCanvas(e.clientX, e.clientY)

      const pts = pointsRef.current
      const lastPt = pts.length > 0 ? pts[pts.length - 1] : null

      if (dragRef.current) {
        // Handle drag in progress — clear guides; Shift snaps the handle direction to the
        // nearest 45° from the anchor being dragged, same as the line-angle constraint.
        if (e.shiftKey) {
          const drag = dragRef.current

          ;({ x, y } = snapToAngle45(drag.startX, drag.startY, x, y))
        }

        props.onSmartGuidesChangeRef.current?.([])
      } else {
        // Shift held + no active handle drag: 45° snap; smart guides disabled while shift is
        // held. Otherwise grid, then smart-guide snap, publishing the guides it found.
        const snap = resolvePenSnap(x, y, e.shiftKey && lastPt ? lastPt : null, snapSettings())

        ;({ x, y } = snap)
        props.onSmartGuidesChangeRef.current?.(snap.guides)
      }

      mousePos.current = { x, y }
      setMousePosState({ x, y })

      // Update close-path indicator
      if (pts.length >= 2) {
        setIsNearFirst(isNearPoint(x, y, pts[0].x, pts[0].y, props.scaleRef.current))
      } else {
        setIsNearFirst(false)
      }

      const drag = dragRef.current

      if (!drag) return

      const dx = x - drag.startX
      const dy = y - drag.startY
      const dist = Math.sqrt(dx * dx + dy * dy)
      const minDist = HANDLE_MIN_DRAG_PX / props.scaleRef.current

      if (dist < minDist && !drag.hasMoved) return

      drag.hasMoved = true

      const updated = [...pointsRef.current]

      updated[drag.pointIndex] = dragHandlePoint(updated[drag.pointIndex], dx, dy)
      pointsRef.current = updated
      forceUpdate((n) => n + 1)
    }

    const onPointerUp = (_e: PointerEvent) => {
      dragRef.current = null
    }

    /**
     * Double-click ends an open path.
     * A dblclick fires after two full click cycles:
     *   pointerdown → pointerup → [click] → pointerdown → pointerup → [dblclick]
     * Both pointerdowns add a point, so pointsRef has a duplicate last entry.
     * We remove it so the path ends at the first-click position.
     */
    const onDblClick = (e: MouseEvent) => {
      if (!isInCanvasArea(e.target)) return

      e.preventDefault()
      e.stopPropagation()

      const pts = pointsRef.current
      // Remove the duplicate point added by the second click of the double-click
      const finalPts = pts.length > 1 ? pts.slice(0, -1) : pts

      if (finalPts.length >= 2) {
        completePath(finalPts, false)
      } else {
        // Not enough points — discard
        resetDrawing(false)
        props.onCancelRef.current?.()
      }
    }

    window.addEventListener('pointerdown', onPointerDown, { capture: true, passive: false })
    window.addEventListener('pointermove', onPointerMove, { capture: true, passive: true })
    window.addEventListener('pointerup', onPointerUp, { capture: false })
    window.addEventListener('dblclick', onDblClick, { capture: true })

    return () => {
      window.removeEventListener('pointerdown', onPointerDown, { capture: true })
      window.removeEventListener('pointermove', onPointerMove, { capture: true })
      window.removeEventListener('pointerup', onPointerUp, { capture: false })
      window.removeEventListener('dblclick', onDblClick, { capture: true })
      // Clear any active smart guides when the overlay unmounts (navigation, tool change, etc.)
      props.onSmartGuidesChangeRef.current?.([])
      // If the overlay unmounts mid-draw (e.g. user switches to a different tool without
      // explicitly finishing), commit the in-progress path as an open shape. Normal exits
      // (Escape, double-click, Enter, close-path) all call completePath() which zeros out
      // pointsRef before unmount, so the length check prevents double-committing.
      // Guard: skip if no new nodes were added beyond the continuation initialPoints —
      // the shape already exists and re-committing unchanged points would trigger an
      // unwanted handlePenPathCompleted cycle that re-enters node-edit mode.
      commitInProgressPath(initialPointsLen)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
